import { createTdWithElement, createTdWithText, createImg, createTableBodyLoadingIndicator } from "../DomUtilities/ElementsCreator";
import { insertBody } from "../DomUtilities/ElementsUtil";
import TablePaginator from "../Pagination/TablePaginator";
import ITableSort from "../Sorting/ITableSort";
import ITablePaginator from "../Pagination/ITablePaginator";
import TableSort from "../Sorting/TableSort";
import ServiceBaseModal from "./ServiceBaseModal";
import Variants from "./Variants/Variants";
import ItemCategory from "../Models/ViewModels/ItemCategory";
import { fetchServicesList } from "../HTTPRequests/ServiceRequests";
import PageModel from "../Models/ViewModels/PageModel";
import openErrorModal from "../Error/ErrorModal";
import CreateServiceModal from "./CreateServiceModal";
import UpdateServiceModal from "./UpdateServiceModal";
import Mediator from "../Utilities/Mediator";
import VariantsDisplayOrderModal from "./VariantsDisplayOrderModal";
import Service from "../Models/ViewModels/Service";

export default class Services implements ITableSort, ITablePaginator {
	table: HTMLElement;
	searchBar: HTMLInputElement;
	paginator: TablePaginator;
	createBtn: HTMLButtonElement;
	modal: ServiceBaseModal;
	sortHandler: TableSort;
	variantsHandler: Variants;

	constructor() {
		this.table = document.getElementById('services');
		this.searchBar = document.querySelector('input.search-bar');
		this.createBtn = document.querySelector('.create-btn');
		this.paginator = new TablePaginator(this);
		this.sortHandler = new TableSort(this);

		this.init();
	}

	private init(): void {
		Mediator.on("serviceCrudOperationFinished", () => this.fetchList());
		this.fetchList();
		this.addSearchInputListener();
		this.sortHandler.addSortingIconsClickListeners();
		this.paginator.addPaginationEventsListeners();
		new CreateServiceModal(this.createBtn).addCreateModalClickListener();;
	}

	public fetchList() {
		this.showLoadingIndicator();
		fetchServicesList(this.SearchValue, this.sortHandler.sortValue, this.sortHandler.sortAscending, this.paginator.currentPage, this.paginator.pageSize)
			.then((response: PageModel<ItemCategory>) => {
				this.updateTable(response);
			}, () => { //backend error
				openErrorModal();
			}).catch(() => { //frontend error
				openErrorModal();
			});
	}

	private showLoadingIndicator() {
		insertBody(this.table, createTableBodyLoadingIndicator());
	}

	private updateTable(data: PageModel<ItemCategory>) {
		this.paginator.currentPage = data.pageNumber;
		this.paginator.total = data.total;
		this.paginator.pageSize = data.pageSize;
		this.renderTable(data.list);
	}

	private renderTable(list: Array<ItemCategory>) {
		insertBody(this.table,
			this.renderBody(list)
		);
		this.paginator.renderFoot();
	}

	private renderBody(itemServiceList: Array<ItemCategory>) {
		const tbody = document.createElement('tbody');
		if (itemServiceList.length) {
			this.variantsHandler = new Variants();
			for (let i = 0; i < itemServiceList.length; i++) {
				const tr = this.createTableRow(itemServiceList[i]);
				tbody.append(tr);
			}
		} else {
			const emptyText = this.table.dataset.empty;
			const tr = document.createElement('tr');
			const td = createTdWithText(emptyText);
			td.colSpan = this.table.querySelectorAll('th').length;
			td.style.textAlign = 'center';
			tr.append(td);
			tbody.append(tr);
		}

		return tbody;
	}

	private createTableRow(itemService: ItemCategory): HTMLTableRowElement {
		const tr = document.createElement('tr');
		const tdItemName = createTdWithText(itemService.name);

		let src: string;
		if (itemService.service.image) {
			src = itemService.service.image;
		} else {
			src = "/assets/no_image_placeholder.png";
		}

		const serviceImage = createImg(src, `${itemService.service.name} ikon`);
		const arrowIcon = document.createElement('i');
		const serviceImageTooltipWrapper = document.createElement('div');
		const serviceImageTooltip = document.createElement('div');
		const tdServiceName = createTdWithElement(serviceImageTooltipWrapper);

		arrowIcon.classList.add("icon-caret-down");
		serviceImageTooltipWrapper.innerText = itemService.service.name;
		serviceImageTooltip.appendChild(serviceImage);
		serviceImageTooltip.appendChild(arrowIcon);
		serviceImageTooltipWrapper.appendChild(serviceImageTooltip);

		const tdPrice = createTdWithText("");
		const tdPTime = createTdWithText("");
		const date = new Date(itemService.service.lastModified);
		const tdDate = createTdWithText(date.toLocaleDateString());
		const imgEdit = createImg('/assets/edit.svg', 'edit icon');
		const imgExpand = createImg('/assets/chevron_left.svg', 'expand more icon');

		tr.classList.add('table__row', 'service-row');
		tr.id = itemService.service.id;
		tr.dataset.itemId = itemService.id;
		imgEdit.id = itemService.service.id;

		tr.appendChild(tdItemName);
		tr.appendChild(tdServiceName);
		tr.append(tdPrice);
		tr.append(tdPTime);
		tr.append(tdDate);

		if (itemService.service.hasVariants) {
			const imgSortVariants = document.createElement('i');
			imgSortVariants.classList.add('icon-sort-by-attributes');
			imgSortVariants.title = "Sorter ydelsesvariant";
			tr.append(createTdWithElement(imgSortVariants, imgExpand, imgEdit));
			new VariantsDisplayOrderModal(itemService.service.id, imgSortVariants);
		} else {
			tr.append(createTdWithElement(imgExpand, imgEdit));
		}

		if (itemService.hasSimpleOrderFlow) {
			new UpdateServiceModal().addRowClickListener(tr);
			tdPrice.innerText = itemService.service.orderCreationPrice;
			tdPTime.innerText = itemService.service.productionTime;

			const guidingPriceTooltip: HTMLDivElement = this.createGuidingPriceTooltip(itemService.service);
			tdPrice.appendChild(guidingPriceTooltip);
		} else {
			new UpdateServiceModal().addIconClickListener(imgEdit);
			this.variantsHandler.init(tr);
		}

		return tr;
	}

	private createGuidingPriceTooltip(service: Service): HTMLDivElement {
		const guidingPriceTooltip = document.createElement('div');
		const guidingPriceTooltipWrapper = document.createElement('div');
		const tooltipList = document.createElement('ul');
		const priceWithoutVat = document.createElement('li');
		const priceWitVat = document.createElement('li');
		const domainPrice = document.createElement('li');

		const priceWithoutVatLabel = document.createElement('span');
		const priceWithoutVatValue = document.createElement('span');
		priceWithoutVatLabel.innerText = this.table.dataset.pricewithoutvat;
		priceWithoutVatValue.innerHTML = `${service.priceWithoutVat} Kr.`;

		const vat = parseFloat(this.table.dataset.vat);
		const priceWithVatLabel = document.createElement('span');
		const priceWithVatValue = document.createElement('span');
		priceWithVatLabel.innerHTML = this.table.dataset.pricewithvat + ` ${(vat * 100).toFixed(0)}%`;
		priceWithVatValue.innerHTML = ` ${service.priceWithVat} Kr.`;

		const domainPriceLabel = document.createElement('span');
		const domainPriceValue = document.createElement('span');
		domainPriceLabel.innerHTML = this.table.dataset.domainprice;
		domainPriceValue.innerHTML = `${service.domainOrderCreationPrice} Kr.`;

		priceWithoutVat.appendChild(priceWithoutVatLabel);
		priceWithoutVat.appendChild(priceWithoutVatValue);
		priceWitVat.appendChild(priceWithVatLabel);
		priceWitVat.appendChild(priceWithVatValue);
		domainPrice.appendChild(domainPriceLabel);
		domainPrice.appendChild(domainPriceValue);
		tooltipList.appendChild(priceWithoutVat);
		tooltipList.appendChild(priceWitVat);
		tooltipList.appendChild(domainPrice);

		const arrowIcon = document.createElement('i');
		arrowIcon.classList.add("icon-caret-down");

		guidingPriceTooltip.appendChild(tooltipList);
		guidingPriceTooltip.appendChild(arrowIcon);
		guidingPriceTooltipWrapper.appendChild(guidingPriceTooltip);

		return guidingPriceTooltipWrapper;
	}

	private get SearchValue() {
		return this.searchBar.value ? this.searchBar.value : "";
	}

	private addSearchInputListener() {
		this.searchBar.oninput = () => {
			this.fetchList();
		}
	}
}
